import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, UserPlus, Search, Mail, Phone, Calendar, Trash2, Edit, Plus, UserCheck, Key, RefreshCw, Eye, EyeOff, Copy, MoreHorizontal, Circle, Hash } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { usePresence } from '@/hooks/usePresence';
import { useStudentManagement } from '@/hooks/useStudentManagement';
import { StudentPrintCard, BulkStudentCardExport } from './StudentPrintCard';

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  created_at: string;
  groups?: { name: string }[];
  avatar_url?: string;
  status?: string;
}

interface UserPresence {
  user_id: string;
  online_at: string;
  status: 'online' | 'offline';
  first_name?: string;
  last_name?: string;
}

interface Group {
  id: string;
  name: string;
  lecturer_id: string;
  student_count: number;
}

export const StudentsManagementSection = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [lecturers, setLecturers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateGroupDialogOpen, setIsCreateGroupDialogOpen] = useState(false);
  const [isAssignStudentDialogOpen, setIsAssignStudentDialogOpen] = useState(false);
  const [isEditStudentDialogOpen, setIsEditStudentDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [isIndividualCredentialsDialogOpen, setIsIndividualCredentialsDialogOpen] = useState(false);
  const [selectedStudentCredentials, setSelectedStudentCredentials] = useState<any>(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [newStudent, setNewStudent] = useState({
    first_name: '',
    last_name: '',
    middle_name: '',
    email: '',
    phone: ''
  });
  const [newGroup, setNewGroup] = useState({
    name: '',
    lecturer_id: ''
  });
  const { toast } = useToast();
  const { onlineUsers } = usePresence();
  const { createStudent } = useStudentManagement();

  // Remove the separate admin presence tracking since we're using the global one
  const isUserOnline = (userId: string): boolean => {
    return onlineUsers[userId] && onlineUsers[userId].length > 0;
  };

  const getLastSeen = (userId: string): Date | null => {
    const userPresences = onlineUsers[userId];
    if (!userPresences || userPresences.length === 0) return null;
    return new Date(userPresences[0].online_at);
  };

  const getStudentAvatar = async (studentId: string): Promise<string | undefined> => {
    try {
      const { data: avatarData } = await supabase.storage
        .from('avatars')
        .list(`${studentId}/`, {
          limit: 1,
          sortBy: { column: 'updated_at', order: 'desc' }
        });

      if (avatarData && avatarData.length > 0) {
        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(`${studentId}/${avatarData[0].name}`);
        return publicUrl;
      }
    } catch (error) {
      console.error('Error fetching avatar:', error);
    }
    return undefined;
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      
      // First get student user IDs from user_roles
      const { data: studentRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'student');

      if (rolesError) throw rolesError;

      if (!studentRoles || studentRoles.length === 0) {
        setStudents([]);
        return;
      }

      const studentIds = studentRoles.map(role => role.user_id);

      // Then get profiles for those users
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, phone, created_at, status')
        .in('id', studentIds);

      if (profilesError) throw profilesError;

      // Get group information and avatars for each student
      const studentsWithExtras = await Promise.all(
        (profiles || []).map(async (student) => {
          const { data: groupData } = await supabase
            .from('group_students')
            .select(`
              groups!group_students_group_id_fkey(
                name
              )
            `)
            .eq('student_id', student.id);

          const avatar_url = await getStudentAvatar(student.id);

          return {
            ...student,
            groups: groupData?.map(g => g.groups).filter(Boolean) || [],
            avatar_url
          };
        })
      );

      setStudents(studentsWithExtras);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել ուսանողների ցանկը",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const { data: groupsData, error: groupsError } = await supabase
        .from('groups')
        .select('*')
        .order('created_at', { ascending: false });

      if (groupsError) throw groupsError;

      // Get student counts for each group
      const groupsWithCounts = await Promise.all(
        (groupsData || []).map(async (group) => {
          const { count, error: countError } = await supabase
            .from('group_students')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', group.id);
          
          return {
            ...group,
            student_count: count || 0
          };
        })
      );

      setGroups(groupsWithCounts);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchLecturers = async () => {
    try {
      const { data: lecturerRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'lecturer');

      if (rolesError) throw rolesError;

      if (!lecturerRoles || lecturerRoles.length === 0) {
        setLecturers([]);
        return;
      }

      const lecturerIds = lecturerRoles.map(role => role.user_id);

      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('id', lecturerIds);

      if (profilesError) throw profilesError;

      setLecturers(profiles || []);
    } catch (error) {
      console.error('Error fetching lecturers:', error);
    }
  };

  const handleViewStudentCredentials = async (studentId: string) => {
    try {
      const { data, error } = await supabase
        .from('student_invitations')
        .select('*')
        .eq('student_id', studentId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      
      if (data && data.length > 0) {
        // Get student details
        const { data: studentData, error: studentError } = await supabase
          .from('profiles')
          .select('first_name, last_name, email')
          .eq('id', studentId)
          .single();
        
        const credentialWithDetails = {
          ...data[0],
          student: studentError ? null : studentData
        };
        
        setSelectedStudentCredentials(credentialWithDetails);
        setIsIndividualCredentialsDialogOpen(true);
      } else {
        toast({
          title: "Ծանուցում",
          description: "Այս ուսանողի համար մուտքի տվյալներ չկան",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Error fetching student credentials:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել մուտքի տվյալները",
        variant: "destructive"
      });
    }
  };

  const handleResetPassword = async (studentId: string) => {
    setIsResettingPassword(true);
    try {
      // Call the edge function to reset password
      const { data, error } = await supabase.functions.invoke('reset-student-password', {
        body: { studentId }
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Password reset failed');
      }

      toast({
        title: "Հաջողություն",
        description: `Գաղտնաբառը վերականգնվեց: ${data.newPassword}`,
        duration: 10000
      });

      // Refresh individual credentials if dialog is open
      if (selectedStudentCredentials) {
        await handleViewStudentCredentials(selectedStudentCredentials.student_id);
      }
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({
        title: "Սխալ",
        description: error.message || "Չհաջողվեց վերականգնել գաղտնաբառը",
        variant: "destructive"
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Պատճենվեց",
      description: "Տեքստը պատճենվեց clipboard-ում",
    });
  };

  useEffect(() => {
    fetchStudents();
    fetchGroups();
    fetchLecturers();
  }, []);

  const handleCreateStudent = async () => {
    const result = await createStudent(
      newStudent.first_name,
      newStudent.last_name,
      newStudent.middle_name,
      newStudent.phone,
      newStudent.email
    );

    if (result.success) {
      setIsCreateDialogOpen(false);
      setNewStudent({ first_name: '', last_name: '', middle_name: '', email: '', phone: '' });
      fetchStudents();
    }
  };

  const handleCreateGroup = async () => {
    try {
      const { data, error } = await supabase
        .from('groups')
        .insert({
          name: newGroup.name.trim(),
          lecturer_id: newGroup.lecturer_id
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Խումբը հաջողությամբ ստեղծվեց"
      });

      setIsCreateGroupDialogOpen(false);
      setNewGroup({ name: '', lecturer_id: '' });
      fetchGroups();
    } catch (error) {
      console.error('Error creating group:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ստեղծել խումբը",
        variant: "destructive"
      });
    }
  };

  const handleAssignStudentToGroup = async (groupId: string) => {
    try {
      // Check if student is already in this group
      const { data: existingRecord } = await supabase
        .from('group_students')
        .select('id')
        .eq('group_id', groupId)
        .eq('student_id', selectedStudentId)
        .single();

      if (existingRecord) {
        toast({
          title: "Տեղեկություն",
          description: "Ուսանողը արդեն գտնվում է այս խմբում",
          variant: "default"
        });
        setIsAssignStudentDialogOpen(false);
        setSelectedStudentId('');
        return;
      }

      const { error } = await supabase
        .from('group_students')
        .insert({
          group_id: groupId,
          student_id: selectedStudentId
        });

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Ուսանողը հաջողությամբ ավելացվեց խմբում"
      });

      setIsAssignStudentDialogOpen(false);
      setSelectedStudentId('');
      fetchStudents();
      fetchGroups();
    } catch (error) {
      console.error('Error assigning student to group:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ավելացնել ուսանողին խմբում",
        variant: "destructive"
      });
    }
  };

  const handleEditStudent = async () => {
    if (!editingStudent) return;
    
    try {
      // Update profile information
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: editingStudent.first_name,
          last_name: editingStudent.last_name,
          phone: editingStudent.phone || null,
          email: editingStudent.email
        })
        .eq('id', editingStudent.id);

      if (profileError) throw profileError;

      // Update email in auth.users if it changed
      const originalStudent = students.find(s => s.id === editingStudent.id);
      if (originalStudent && originalStudent.email !== editingStudent.email) {
        const { error: authError } = await supabase.auth.admin.updateUserById(editingStudent.id, {
          email: editingStudent.email
        });

        if (authError) throw authError;
      }

      toast({
        title: "Հաջողություն",
        description: "Ուսանողի տվյալները հաջողությամբ թարմացվեցին"
      });

      setIsEditStudentDialogOpen(false);
      setEditingStudent(null);
      fetchStudents();
    } catch (error) {
      console.error('Error updating student:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց թարմացնել ուսանողի տվյալները",
        variant: "destructive"
      });
    }
  };

  const handleUpdateStudentStatus = async (studentId: string, newStatus: string) => {
    try {
      const { data, error } = await supabase.rpc('update_student_status_with_blocking', {
        student_id: studentId,
        new_status: newStatus
      });

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Ուսանողի կարգավիճակը հաջողությամբ թարմացվեց"
      });

      fetchStudents();
    } catch (error: any) {
      console.error('Error updating student status:', error);
      toast({
        title: "Սխալ", 
        description: error.message || "Չհաջողվեց թարմացնել ուսանողի կարգավիճակը",
        variant: "destructive"
      });
    }
  };

  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'expelled':
        return 'destructive';
      case 'suspended':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'active':
        return 'Ակտիվ';
      case 'completed':
        return 'Ավարտած';
      case 'expelled':
        return 'Հեռացված';
      case 'suspended':
        return 'Կասեցված';
      default:
        return 'Ակտիվ';
    }
  };

  const handleDeleteStudent = async (studentId: string) => {
    try {
      // Use the safe delete function that handles all deletions properly
      const { data, error } = await supabase.rpc('delete_student_safely', {
        student_user_id: studentId
      });

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Ուսանողը հաջողությամբ ջնջվեց"
      });

      fetchStudents();
      fetchGroups(); // Also refresh groups to update student counts
    } catch (error: any) {
      console.error('Error deleting student:', error);
      toast({
        title: "Սխալ",
        description: error.message || "Չհաջողվեց ջնջել ուսանողին",
        variant: "destructive"
      });
    }
  };

  const handleDeleteStudentAvatar = async (studentId: string) => {
    try {
      // Get all avatar files for this student
      const { data: avatarFiles } = await supabase.storage
        .from('avatars')
        .list(`${studentId}/`, {
          limit: 100,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (avatarFiles && avatarFiles.length > 0) {
        // Delete all avatar files for this student
        const filePaths = avatarFiles.map(file => `${studentId}/${file.name}`);
        const { error } = await supabase.storage
          .from('avatars')
          .remove(filePaths);

        if (error) throw error;

        toast({
          title: "Հաջողություն",
          description: "Ավատարը հաջողությամբ ջնջվեց"
        });

        // Update the editing student's avatar_url
        if (editingStudent && editingStudent.id === studentId) {
          setEditingStudent({ ...editingStudent, avatar_url: undefined });
        }

        // Refresh students list to update avatars
        fetchStudents();
      }
    } catch (error: any) {
      console.error('Error deleting avatar:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ջնջել ավատարը",
        variant: "destructive"
      });
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || student.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('hy-AM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Card className="border-border/70 bg-card/80">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="text-muted-foreground flex items-center gap-2">
            <Users className="h-5 w-5" />
            Ուսանողների կառավարում ({students.length})
          </CardTitle>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary h-4 w-4" />
              <Input
                placeholder="Փնտրել ուսանողի..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-muted border-border/70 text-foreground"
              />
            </div>
            <Dialog open={isCreateGroupDialogOpen} onOpenChange={setIsCreateGroupDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Նոր խումբ
                </Button>
              </DialogTrigger>
              <DialogContent className="border-border/70 bg-card/80">
                <DialogHeader>
                  <DialogTitle className="text-muted-foreground">Ստեղծել նոր խումբ</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Խմբի անուն</Label>
                    <Input
                      value={newGroup.name}
                      onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                      className="bg-muted border-border/70 text-foreground"
                      placeholder="Մուտքագրեք խմբի անունը"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Դասախոս</Label>
                    <Select value={newGroup.lecturer_id} onValueChange={(value) => setNewGroup({ ...newGroup, lecturer_id: value })}>
                      <SelectTrigger className="bg-muted border-border/70 text-foreground">
                        <SelectValue placeholder="Ընտրել դասախոս" />
                      </SelectTrigger>
                      <SelectContent className="bg-muted border-border/70">
                        {lecturers.map((lecturer) => (
                          <SelectItem key={lecturer.id} value={lecturer.id} className="text-foreground">
                            {lecturer.first_name} {lecturer.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleCreateGroup}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={!newGroup.name || !newGroup.lecturer_id}
                  >
                    Ստեղծել խումբ
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Նոր ուսանող
                </Button>
              </DialogTrigger>
              <DialogContent className="border-border/70 bg-card/80">
                <DialogHeader>
                  <DialogTitle className="text-muted-foreground">Ստեղծել նոր ուսանող</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Անուն</Label>
                    <Input
                      value={newStudent.first_name}
                      onChange={(e) => setNewStudent({ ...newStudent, first_name: e.target.value })}
                      className="bg-muted border-border/70 text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Ազգանուն</Label>
                    <Input
                      value={newStudent.last_name}
                      onChange={(e) => setNewStudent({ ...newStudent, last_name: e.target.value })}
                      className="bg-muted border-border/70 text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Հայրանուն</Label>
                    <Input
                      value={newStudent.middle_name}
                      onChange={(e) => setNewStudent({ ...newStudent, middle_name: e.target.value })}
                      className="bg-muted border-border/70 text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Էլ. փոստ</Label>
                    <Input
                      type="email"
                      value={newStudent.email}
                      onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                      className="bg-muted border-border/70 text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Հեռախոս</Label>
                    <Input
                      value={newStudent.phone}
                      onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })}
                      className="bg-muted border-border/70 text-foreground"
                    />
                  </div>
                  <Button
                    onClick={handleCreateStudent}
                    className="w-full bg-primary hover:bg-primary/90"
                    disabled={!newStudent.first_name || !newStudent.last_name || !newStudent.email}
                  >
                    Ստեղծել ուսանող
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Edit Student Dialog */}
            <Dialog open={isEditStudentDialogOpen} onOpenChange={setIsEditStudentDialogOpen}>
              <DialogContent className="border-border/70 bg-card/80 max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-muted-foreground">Խմբագրել ուսանողին</DialogTitle>
                </DialogHeader>
                {editingStudent && (
                  <div className="space-y-4">
                    {/* Avatar Section */}
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Ավատար</Label>
                      <div className="flex items-center gap-4">
                        <Avatar className="h-16 w-16">
                          <AvatarImage src={editingStudent.avatar_url} alt={`${editingStudent.first_name} ${editingStudent.last_name}`} />
                          <AvatarFallback className="bg-primary/10 text-primary text-lg">
                            {editingStudent.first_name?.charAt(0)}{editingStudent.last_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        {editingStudent.avatar_url && (
                          <Button
                            onClick={() => handleDeleteStudentAvatar(editingStudent.id)}
                            variant="outline"
                            size="sm"
                            className="bg-red-600 hover:bg-red-700 text-primary-foreground border-red-500"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Ջնջել ավատարը
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Անուն</Label>
                      <Input
                        value={editingStudent.first_name}
                        onChange={(e) => setEditingStudent({ ...editingStudent, first_name: e.target.value })}
                        className="bg-muted border-border/70 text-foreground"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Ազգանուն</Label>
                      <Input
                        value={editingStudent.last_name}
                        onChange={(e) => setEditingStudent({ ...editingStudent, last_name: e.target.value })}
                        className="bg-muted border-border/70 text-foreground"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Էլ. փոստ</Label>
                      <Input
                        type="email"
                        value={editingStudent.email}
                        onChange={(e) => setEditingStudent({ ...editingStudent, email: e.target.value })}
                        className="bg-muted border-border/70 text-foreground"
                      />
                      <p className="text-xs text-primary">Էլ. փոստի փոփոխությունը միայն ադմինի համար</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">User ID</Label>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 flex-1 bg-muted border border-border/70 rounded-md px-3 py-2">
                          <Hash className="h-4 w-4 text-primary" />
                          <span className="text-muted-foreground text-sm">{editingStudent.id}</span>
                        </div>
                        <Button
                          onClick={() => copyToClipboard(editingStudent.id)}
                          size="sm"
                          variant="ghost"
                          className="text-primary hover:text-muted-foreground"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">User ID-ն հնարավոր չէ փոխել</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Հեռախոս</Label>
                      <Input
                        value={editingStudent.phone || ''}
                        onChange={(e) => setEditingStudent({ ...editingStudent, phone: e.target.value })}
                        className="bg-muted border-border/70 text-foreground"
                      />
                    </div>
                    <Button
                      onClick={handleEditStudent}
                      className="w-full bg-primary hover:bg-primary/90"
                      disabled={!editingStudent.first_name || !editingStudent.last_name}
                    >
                      Պահպանել փոփոխությունները
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Individual Student Credentials Dialog */}
            <Dialog open={isIndividualCredentialsDialogOpen} onOpenChange={setIsIndividualCredentialsDialogOpen}>
              <DialogContent className="border-border/70 bg-card/80 max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-muted-foreground">
                    Ուսանողի մուտքի տվյալներ
                  </DialogTitle>
                </DialogHeader>
                {selectedStudentCredentials && (
                  <div className="space-y-4">
                    <div className="p-4 bg-muted rounded-lg border border-border/70">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="text-foreground font-medium text-lg">
                            {selectedStudentCredentials.student?.first_name} {selectedStudentCredentials.student?.last_name}
                          </h4>
                          <p className="text-muted-foreground">
                            {selectedStudentCredentials.student_email}
                          </p>
                        </div>
                        <Badge className={`${
                          selectedStudentCredentials.password_changed 
                            ? 'bg-primary/10 text-primary border-border/70' 
                            : 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30'
                        }`}>
                          {selectedStudentCredentials.password_changed ? 'Փոխված' : 'Ժամանակավոր'}
                        </Badge>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <Label className="text-muted-foreground text-sm">Էլեկտրոնային փոստ</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="bg-muted px-3 py-2 rounded text-foreground flex-1">
                              {selectedStudentCredentials.student_email}
                            </code>
                            <Button
                              onClick={() => copyToClipboard(selectedStudentCredentials.student_email)}
                              size="sm"
                              variant="ghost"
                              className="text-primary hover:text-muted-foreground"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-muted-foreground text-sm">Ժամանակավոր գաղտնաբառ</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="bg-muted px-3 py-2 rounded text-foreground flex-1">
                              {showPassword ? selectedStudentCredentials.temporary_password : '••••••••'}
                            </code>
                            <Button
                              onClick={() => setShowPassword(!showPassword)}
                              size="sm"
                              variant="ghost"
                              className="text-primary hover:text-muted-foreground"
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button
                              onClick={() => copyToClipboard(selectedStudentCredentials.temporary_password)}
                              size="sm"
                              variant="ghost"
                              className="text-primary hover:text-muted-foreground"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-muted-foreground text-sm">Ստեղծման ամսաթիվ</Label>
                            <p className="text-muted-foreground text-sm mt-1">
                              {new Date(selectedStudentCredentials.created_at).toLocaleString('hy-AM')}
                            </p>
                          </div>
                          {selectedStudentCredentials.used_at && (
                            <div>
                              <Label className="text-muted-foreground text-sm">Օգտագործման ամսաթիվ</Label>
                              <p className="text-muted-foreground text-sm mt-1">
                                {new Date(selectedStudentCredentials.used_at).toLocaleString('hy-AM')}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-3 pt-4 border-t border-border">
                          <Button
                            onClick={() => handleResetPassword(selectedStudentCredentials.student_id)}
                            disabled={isResettingPassword}
                            className="bg-yellow-600 hover:bg-yellow-700"
                          >
                            <RefreshCw className={`h-4 w-4 mr-2 ${isResettingPassword ? 'animate-spin' : ''}`} />
                            Վերականգնել գաղտնաբառ
                          </Button>
                          <Button
                            onClick={() => setIsIndividualCredentialsDialogOpen(false)}
                            variant="outline"
                            className="font-mono"
                          >
                            Փակել
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            
            {/* Assign Student to Group Dialog */}
            <Dialog open={isAssignStudentDialogOpen} onOpenChange={setIsAssignStudentDialogOpen}>
              <DialogContent className="border-border/70 bg-card/80">
                <DialogHeader>
                  <DialogTitle className="text-muted-foreground">Ուսանողին ավելացնել խմբում</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Ընտրել խումբ</Label>
                    <div className="grid gap-2">
                      {groups.map((group) => (
                        <Button
                          key={group.id}
                          variant="outline"
                          className="justify-start bg-muted border-border/70 text-primary-foreground hover:bg-primary/20"
                          onClick={() => handleAssignStudentToGroup(group.id)}
                        >
                          <Users className="h-4 w-4 mr-2" />
                          {group.name} ({group.student_count} ուսանող)
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-6 min-w-0">
          <div className="-mx-1 overflow-x-auto px-1 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsList className="inline-flex h-auto min-w-max justify-start gap-1 border border-border/70 bg-muted/70 p-1.5 sm:grid sm:w-full sm:min-w-0 sm:grid-cols-5">
            <TabsTrigger value="all" className="min-h-11 shrink-0 rounded-md px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-2 sm:text-sm">
              Բոլորը ({students.length})
            </TabsTrigger>
            <TabsTrigger value="active" className="min-h-11 shrink-0 rounded-md px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-2 sm:text-sm">
              Ակտիվ ({students.filter(s => s.status === 'active' || !s.status).length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="min-h-11 shrink-0 rounded-md px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-2 sm:text-sm">
              Ավարտած ({students.filter(s => s.status === 'completed').length})
            </TabsTrigger>
            <TabsTrigger value="expelled" className="min-h-11 shrink-0 rounded-md px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-2 sm:text-sm">
              Հեռացված ({students.filter(s => s.status === 'expelled').length})
            </TabsTrigger>
            <TabsTrigger value="suspended" className="min-h-11 shrink-0 rounded-md px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-2 sm:text-sm">
              Կասեցված ({students.filter(s => s.status === 'suspended').length})
            </TabsTrigger>
          </TabsList>
          </div>
          
          {filteredStudents.length > 0 && (
            <div className="mt-4 mb-4">
              <BulkStudentCardExport students={filteredStudents} />
            </div>
          )}
          
          <TabsContent value={statusFilter} className="mt-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Բեռնվում է...
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm ? 'Ուսանող չի գտնվել' : 'Ուսանողներ դեռ չկան'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
              <TableHeader>
                <TableRow className="border-border/70">
                  <TableHead className="text-muted-foreground">Ավատար</TableHead>
                  <TableHead className="text-muted-foreground">Անուն Ազգանուն</TableHead>
                  <TableHead className="text-muted-foreground">Ստատուս</TableHead>
                  <TableHead className="text-muted-foreground">Էլ. փոստ</TableHead>
                  <TableHead className="text-muted-foreground">Հեռախոս</TableHead>
                  <TableHead className="text-muted-foreground">Խմբեր</TableHead>
                  <TableHead className="text-muted-foreground">Գրանցման ամսաթիվ</TableHead>
                  <TableHead className="text-muted-foreground">Գործողություններ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStudents.map((student) => {
                  const isOnline = isUserOnline(student.id);
                  const lastSeen = getLastSeen(student.id);
                  
                  return (
                    <TableRow key={student.id} className="border-border/70">
                       <TableCell>
                         <Avatar className="h-10 w-10">
                           <AvatarImage src={student.avatar_url} alt={`${student.first_name} ${student.last_name}`} />
                           <AvatarFallback className="bg-primary/10 text-primary">
                             {student.first_name?.charAt(0)}{student.last_name?.charAt(0)}
                           </AvatarFallback>
                         </Avatar>
                       </TableCell>
                       <TableCell className="text-muted-foreground">
                         {student.first_name} {student.last_name}
                       </TableCell>
                       <TableCell>
                         <Badge variant={getStatusBadgeVariant(student.status)} className="font-mono">
                           {getStatusLabel(student.status)}
                         </Badge>
                       </TableCell>
                       <TableCell>
                      <div className="flex items-center gap-2">
                        <Circle 
                          className={`h-3 w-3 transition-all duration-500 ${
                            isOnline 
                              ? 'fill-green-500 text-green-500 animate-pulse drop-shadow-[0_0_6px_#22c55e]' 
                              : 'fill-gray-400 text-muted-foreground'
                          }`}
                        />
                        <span className={`text-sm transition-all duration-500 ${
                          isOnline ? 'text-primary font-semibold' : 'text-muted-foreground'
                        }`}>
                          {isOnline ? 'Օնլայն' : 'Օֆլայն'}
                        </span>
                        {!isOnline && lastSeen && (
                          <span className="text-xs text-muted-foreground animate-fade-in">
                            {lastSeen.toLocaleString('hy-AM', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        )}
                      </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-primary" />
                          {student.email}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {student.phone ? (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-primary" />
                            {student.phone}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {student.groups && student.groups.length > 0 ? (
                            student.groups.map((group, index) => (
                              <Badge
                                key={index}
                                className="bg-blue-500/20 text-blue-300 border-blue-400/30 text-xs"
                              >
                                {group.name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-muted-foreground text-sm">Խումբ չկա</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-primary" />
                          {formatDate(student.created_at)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 text-primary hover:bg-green-500/20">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="border-border/70 bg-card/80">
                            <DropdownMenuItem 
                              onClick={() => handleViewStudentCredentials(student.id)}
                              className="text-primary focus:text-muted-foreground focus:bg-green-400/10"
                            >
                              <Key className="h-4 w-4 mr-2" />
                              Մուտքի տվյալներ
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                setEditingStudent(student);
                                setIsEditStudentDialogOpen(true);
                              }}
                              className="text-blue-400 focus:text-blue-300 focus:bg-blue-400/10"
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Խմբագրել
                            </DropdownMenuItem>
                            <StudentPrintCard student={student} />
                             <DropdownMenuItem
                               onClick={() => {
                                 setSelectedStudentId(student.id);
                                 setIsAssignStudentDialogOpen(true);
                               }}
                               className="text-yellow-400 focus:text-yellow-300 focus:bg-yellow-400/10"
                             >
                               <UserCheck className="h-4 w-4 mr-2" />
                               Խումբ կցել
                             </DropdownMenuItem>
                             <DropdownMenuItem
                               onClick={() => handleUpdateStudentStatus(student.id, 'active')}
                               className="text-primary focus:text-muted-foreground focus:bg-green-400/10"
                               disabled={student.status === 'active'}
                             >
                               <Circle className="h-4 w-4 mr-2 fill-green-500" />
                               Ակտիվ դարձնել
                             </DropdownMenuItem>
                             <DropdownMenuItem
                               onClick={() => handleUpdateStudentStatus(student.id, 'completed')}
                               className="text-blue-400 focus:text-blue-300 focus:bg-blue-400/10"
                               disabled={student.status === 'completed'}
                             >
                               <UserCheck className="h-4 w-4 mr-2" />
                               Ավարտած նշել
                             </DropdownMenuItem>
                             <DropdownMenuItem
                               onClick={() => handleUpdateStudentStatus(student.id, 'expelled')}
                               className="text-red-400 focus:text-red-300 focus:bg-red-400/10"
                               disabled={student.status === 'expelled'}
                             >
                               <UserCheck className="h-4 w-4 mr-2" />
                               Հեռացված նշել
                             </DropdownMenuItem>
                             <DropdownMenuItem
                               onClick={() => handleUpdateStudentStatus(student.id, 'suspended')}
                               className="text-orange-400 focus:text-orange-300 focus:bg-orange-400/10"
                               disabled={student.status === 'suspended'}
                             >
                               <Circle className="h-4 w-4 mr-2" />
                               Կասեցված նշել
                             </DropdownMenuItem>
                             <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <DropdownMenuItem
                                  onSelect={(e) => e.preventDefault()}
                                  className="text-red-400 hover:bg-red-500/20 cursor-pointer"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Ջնջել
                                </DropdownMenuItem>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="border-border/70 bg-card/80">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-muted-foreground">
                                    Հաստատել ջնջումը
                                  </AlertDialogTitle>
                                  <AlertDialogDescription className="text-muted-foreground">
                                    Դուք վստահ եք, որ ցանկանում եք ջնջել "{student.first_name} {student.last_name}" ուսանողին? Այս գործողությունը անվերադարձ է:
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-muted border-border/70 text-muted-foreground hover:bg-muted">
                                    Չեղարկել
                                  </AlertDialogCancel>
                                  <AlertDialogAction 
                                    onClick={() => handleDeleteStudent(student.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Ջնջել
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
                </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
